import type { ApiResponse } from '@/features/auth/services/authApi';
import type { AuthUser } from '@/features/auth/stores/authStore';
import apiClient from '@/shared/services/api';

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  otherNames: string;
  phoneNumber?: string;
  userName: string;
  roleName: string;
  agronomistRegions?: string[];
  departmentId?: string | null;
}

export interface UpdateUserRequest {
  userId: string;
  email?: string;
  firstName?: string;
  otherNames?: string;
  phoneNumber?: string;
  isActive?: boolean;
  departmentId?: string | null;
  mfaEnabled?: boolean;
}

export interface DeactivateUserRequest {
  userId: string;
  reason: string;
}

export interface AssignRoleRequest {
  userId: string;
  // roles: string[];
  roleName: string;
  agronomistRegions?: string[];
}

export interface UpdateAgronomistRegionsRequest {
  userId: string;
  agronomistRegions: string[];
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface ApproverUser {
  id: string;
  firstName: string;
  otherNames: string;
}

export const usersApi = {
  getAllUsers: async (pageNumber = 1, pageSize = 20, search?: string): Promise<PagedResult<AuthUser>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<AuthUser>>>(
      '/Users',
      { params: { pageNumber, pageSize, search } }
    );
    return data.data;
  },

  getUserById: async (id: string): Promise<AuthUser> => {
    const { data } = await apiClient.get<ApiResponse<AuthUser>>(
      `/admin/Users/${id}`
    );
    return data.data;
  },

  createUser: async (payload: CreateUserRequest): Promise<AuthUser> => {
    const { data } = await apiClient.post<ApiResponse<AuthUser>>(
      '/Users/register',
      payload
    );
    if (!data.success || !data.data) {
      throw new Error(data.message || 'Failed to create user');
    }
    return data.data;
  },

   updateUser: async (
    id: string,
    payload: UpdateUserRequest
  ): Promise<AuthUser> => {
    const { data } = await apiClient.put<ApiResponse<AuthUser>>(
      `/Users/${id}`,
      payload
    );
    return data.data;
  },

    deactivateUser: async (
    id: string,
    payload: DeactivateUserRequest
  ): Promise<void> => {
    await apiClient.patch(`/Users/${id}/deactivate`, payload);
  },

  reactivateUser: async (id: string): Promise<void> => {
    await apiClient.patch(`/Users/${id}/reactivate`);
  },

  assignRole: async (
    id: string,
    payload: AssignRoleRequest
  ): Promise<void> => {
    await apiClient.post(`/Users/${id}/roles`, payload);
  },

  updateAgronomistRegions: async (
    id: string,
    payload: UpdateAgronomistRegionsRequest
  ): Promise<void> => {
    await apiClient.put(`/Users/${id}/roles/agronomist-regions`, payload);
  },

  removeRole: async (
    id: string,
    payload: AssignRoleRequest
  ): Promise<void> => {
    await apiClient.delete(`/Users/${id}/roles`, { data: payload });
  },

  getUsersByPermission: async (permission: string): Promise<ApproverUser[]> => {
    const { data } = await apiClient.get<ApiResponse<ApproverUser[]>>(
      '/Users/by-permission',
      { params: { permission } }
    );
    return data.data;
  },
};
